/**
 * §9.3 Book a Live Demo — interactive scheduler mock.
 *
 * The scheduler UI is interactive (topic + date + slot + contact
 * form) but there is no live calendar integration in this Phase.
 * Submission POSTs to the existing /api/leads endpoint with
 * kind: 'demo' and the selected slot embedded in the notes field.
 */

import React, { useMemo, useState } from 'react'
import clsx from 'clsx'
import { toast } from 'sonner'
import {
  Sparkles, Bot, LineChart, Building2, ShieldCheck, Calendar, Clock, Globe,
  ChevronRight, ChevronLeft, Loader2, CheckCircle2, ArrowRight,
} from 'lucide-react'
import {
  Container, Eyebrow, SectionHeading, GradientButton,
} from '@/components/landing/design-system'
import { DEMO_TOPICS, DEMO_SLOTS } from '../data'
import { TONE } from '../_shared'

const TOPIC_ICONS = { Sparkles, Bot, LineChart, Building2, ShieldCheck }

function nextBusinessDays(count = 7) {
  const out = []
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  while (out.length < count) {
    d.setDate(d.getDate() + 1)
    const dow = d.getDay()
    if (dow !== 0 && dow !== 6) out.push(new Date(d))
  }
  return out
}

export default function Demo() {
  const days = useMemo(() => nextBusinessDays(7), [])
  const [topic, setTopic] = useState(DEMO_TOPICS[0].id)
  const [dayIdx, setDayIdx] = useState(0)
  const [slot, setSlot] = useState(DEMO_SLOTS[1])
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [company, setCompany] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)

  const currentTopic = DEMO_TOPICS.find((t) => t.id === topic) || DEMO_TOPICS[0]
  const tone = TONE[currentTopic.tone] || TONE.orange
  const TopicIcon = TOPIC_ICONS[currentTopic.iconKey] || Sparkles

  const submit = async (e) => {
    e.preventDefault()
    if (!name || !email || !company) return toast.error('Name, email and company are required.')
    setBusy(true)
    try {
      const chosen = days[dayIdx]
      const dayLabel = chosen ? chosen.toISOString().slice(0, 10) : 'unset'
      const r = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          kind: 'demo',
          name, email, company,
          use_case: `Demo topic: ${currentTopic.label} (${currentTopic.mins} min)`,
          notes:   `Requested slot: ${dayLabel} at ${slot} UTC`,
        }),
      })
      const j = await r.json()
      if (!r.ok) throw new Error(j.error || 'submission failed')
      setDone(true)
      toast.success('Demo request received. We will confirm within 4 business hours.')
    } catch (err) {
      toast.error(err.message || 'Something went wrong')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section id="demo" className="relative py-24 md:py-28 scroll-mt-24">
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow tone="green">Book a Live Demo</Eyebrow>
          <SectionHeading align="center"
            title={<>See Kalki Ai Cloud{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">in your own timezone.</span></>}
            subtitle="Pick a topic, a day and a slot — we&apos;ll confirm within four business hours with a video-call link and a pre-populated agenda tailored to your business."
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* LEFT — topic + date + slot */}
          <div className="lg:col-span-7 space-y-4">
            {/* Topics */}
            <div className="relative rounded-2xl border border-white/10 bg-white/[0.02] p-4 md:p-5 backdrop-blur-xl overflow-hidden">
              <div className="pointer-events-none absolute inset-0 opacity-40"
                style={{ background: `radial-gradient(80% 45% at 20% 0%, rgba(${tone.rgb},0.10), transparent 70%)` }} />
              <div className="relative flex items-center justify-between mb-3">
                <div className="text-[10.5px] font-mono uppercase tracking-wider text-zinc-500">Step 01 · choose topic</div>
                <span className="inline-flex items-center gap-1 text-[10.5px] font-mono text-emerald-400">
                  <Clock className="h-3 w-3" /> ~{currentTopic.mins} min
                </span>
              </div>
              <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
                {DEMO_TOPICS.map((t) => {
                  const on = topic === t.id
                  const tt = TONE[t.tone] || TONE.orange
                  const Icon = TOPIC_ICONS[t.iconKey] || Sparkles
                  return (
                    <button key={t.id} type="button" onClick={() => setTopic(t.id)}
                      className={clsx('flex flex-col items-start gap-1.5 rounded-lg border px-2.5 py-2 text-left transition-colors',
                        !on && 'border-white/10 bg-white/[0.02] hover:border-white/20',
                      )}
                      style={on ? { borderColor: `${tt.hex}66`, background: `${tt.hex}18` } : undefined}>
                      <span className="inline-flex h-7 w-7 items-center justify-center rounded-md border"
                        style={{ borderColor: `rgba(${tt.rgb},0.35)`, background: `rgba(${tt.rgb},0.08)`, color: tt.hex }}>
                        <Icon strokeWidth={1.8} style={{ width: 13, height: 13 }} />
                      </span>
                      <span className={clsx('text-[11.5px] font-semibold leading-tight', on ? 'text-white' : 'text-zinc-300')}>{t.label}</span>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Date + slot */}
            <div className="relative rounded-2xl border border-white/10 bg-white/[0.02] p-4 md:p-5 backdrop-blur-xl overflow-hidden">
              <div className="pointer-events-none absolute inset-0 opacity-40"
                style={{ background: 'radial-gradient(80% 45% at 20% 0%, rgba(34,197,94,0.10), transparent 70%)' }} />
              <div className="relative flex items-center justify-between mb-3">
                <div className="text-[10.5px] font-mono uppercase tracking-wider text-zinc-500">Step 02 · pick a day &amp; slot</div>
                <span className="inline-flex items-center gap-1 text-[10.5px] font-mono text-zinc-400">
                  <Globe className="h-3 w-3" /> UTC (00:00 offset)
                </span>
              </div>

              {/* Day carousel */}
              <div className="relative flex items-center gap-2">
                <button type="button" onClick={() => setDayIdx((i) => Math.max(0, i - 1))}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-white/10 bg-white/[0.02] text-zinc-400 hover:text-white shrink-0">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <div className="flex-1 grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-7 gap-1.5">
                  {days.map((d, i) => {
                    const on = dayIdx === i
                    const dayLabel = d.toLocaleDateString('en-US', { weekday: 'short' })
                    return (
                      <button key={i} type="button" onClick={() => setDayIdx(i)}
                        className={clsx('flex flex-col items-center rounded-lg border py-2 transition-colors',
                          on
                            ? 'border-brand-gold/50 bg-brand-gold/15 text-white'
                            : 'border-white/10 bg-white/[0.02] text-zinc-300 hover:border-white/20',
                        )}>
                        <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{dayLabel}</span>
                        <span className="text-[15px] font-semibold">{d.getDate()}</span>
                        <span className="text-[9px] font-mono text-zinc-500">{d.toLocaleDateString('en-US', { month: 'short' })}</span>
                      </button>
                    )
                  })}
                </div>
                <button type="button" onClick={() => setDayIdx((i) => Math.min(days.length - 1, i + 1))}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-white/10 bg-white/[0.02] text-zinc-400 hover:text-white shrink-0">
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>

              {/* Slots */}
              <div className="relative mt-3 grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                {DEMO_SLOTS.map((s) => {
                  const on = slot === s
                  return (
                    <button key={s} type="button" onClick={() => setSlot(s)}
                      className={clsx('flex items-center justify-center gap-1.5 rounded-lg border py-2 text-[12.5px] font-mono transition-colors',
                        on
                          ? 'border-brand-gold/50 bg-brand-gold/15 text-brand-gold'
                          : 'border-white/10 bg-white/[0.02] text-zinc-300 hover:border-white/20',
                      )}>
                      <Clock className="h-3 w-3" /> {s} UTC
                    </button>
                  )
                })}
              </div>
            </div>
          </div>

          {/* RIGHT — summary + contact form */}
          <div className="lg:col-span-5">
            <form onSubmit={submit}
              className="relative rounded-2xl border p-5 md:p-6 backdrop-blur-xl overflow-hidden flex flex-col gap-4 h-full"
              style={{ borderColor: `${tone.hex}55`, background: `linear-gradient(180deg, ${tone.hex}0F, rgba(0,0,0,0.02))` }}>
              <div className="pointer-events-none absolute inset-0 opacity-70"
                style={{ background: `radial-gradient(60% 40% at 20% 0%, rgba(${tone.rgb},0.14), transparent 70%)` }} />

              {/* summary */}
              <div className="relative rounded-lg border border-white/10 bg-black/40 p-3">
                <div className="flex items-center gap-2">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-md border"
                    style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}>
                    <TopicIcon strokeWidth={1.8} style={{ width: 14, height: 14 }} />
                  </span>
                  <div className="flex-1">
                    <div className="text-[13px] font-semibold text-white leading-tight">{currentTopic.label}</div>
                    <div className="text-[10.5px] font-mono text-zinc-500">{days[dayIdx]?.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })} · {slot} UTC</div>
                  </div>
                </div>
              </div>

              {done ? (
                <div className="relative flex flex-col items-center gap-3 py-8 text-center">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-emerald-400/50 bg-emerald-400/[0.12] text-emerald-400">
                    <CheckCircle2 className="h-6 w-6" />
                  </span>
                  <div>
                    <div className="text-[15px] font-semibold text-white">Demo request received</div>
                    <p className="text-[12.5px] text-zinc-400 mt-1 max-w-xs">A team member will confirm the calendar invite within 4 business hours.</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="relative grid grid-cols-1 gap-2">
                    <Field label="Full name"      value={name}    onChange={setName}    autoComplete="name" />
                    <Field label="Business email" value={email}   onChange={setEmail}   autoComplete="email" type="email" />
                    <Field label="Company"         value={company} onChange={setCompany} autoComplete="organization" />
                  </div>
                  <GradientButton disabled={busy} onClick={submit}
                    href={undefined}
                    className="relative w-full justify-center">
                    {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <><Calendar className="h-4 w-4" /> Request demo</>}
                  </GradientButton>
                  <div className="relative text-[11px] text-zinc-500 leading-relaxed text-center">
                    By submitting, you agree to our privacy handling. No cold emails — you&apos;ll receive one confirmation with a calendar invite.
                  </div>
                </>
              )}
            </form>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl px-5 md:px-6 py-4 flex flex-col md:flex-row items-start md:items-center gap-3">
          <span className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-emerald-400">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> illustrative scheduler
          </span>
          <p className="text-sm text-zinc-300 leading-relaxed">
            Live calendar integration is being rolled out. In the meantime, submissions land directly in the enterprise desk queue and are answered by a named account executive within four business hours.
            <a href="#sales" className="ml-1 text-brand-gold font-semibold hover:brightness-125 inline-flex items-center gap-1">Prefer email? <ArrowRight className="h-3 w-3" /></a>
          </p>
        </div>
      </Container>
    </section>
  )
}

function Field({ label, value, onChange, type = 'text', autoComplete }) {
  return (
    <label className="block">
      <div className="text-[10.5px] font-mono uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-[13px] text-white outline-none focus:border-brand-gold/50 focus:ring-1 focus:ring-brand-gold/30 transition-colors placeholder-zinc-600"
      />
    </label>
  )
}
